// The app shell: sign-in, or the (currently empty) workspace.
//
// M1 scope. The canvas, grid calibration and the asset search all land on top of this; what exists here is the
// auth boundary and the honest empty state.
import { lazy, Suspense, useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { isConfigured, missingConfig, supabase } from "./lib/supabase";
import { SetPassword, SignIn } from "./ui/auth";
import { RESET_PATH, WELCOME_PATH } from "./lib/auth-rules";
import { cameFromSeat, joinToken, myPlay, pendingInvite, PLAY_CHARACTERS_PATH, PLAY_PATH, rememberedRole, rememberRole, roleOf, type Role } from "./lib/players";
import { loadCurrentCampaign } from "./lib/current-campaign";
// ⚠ TEMPORARY (owner, 2026-09-16): the Feedback pill on every page, "until the system is locked in". Mounted here, ONCE,
// on every surface but the sign-in pane; remove these two lines and the file when he says so. What's New stays.
import { FeedbackButton } from "./ui/feedback-button";
import { DiceLayer } from "./ui/dice-layer";
import { WhatsNew } from "./ui/whats-new";
import { TourHost } from "./ui/tour";
import { TutorialsPage } from "./ui/tutorials-page";
import { isNewPlayer } from "./lib/tours";

/**
 * ⚠ THE THREE SURFACES ARE SPLIT BECAUSE THE PLAYERS' ONE IS THE SLOWEST DEVICE IN THE ROOM.
 *
 * Imported eagerly, every one of these lands in a single bundle — so a share link on a tablet or a television
 * at the table downloaded the ENTIRE GM application (composite editor, asset search, the connectors, the UVTT
 * importer, the whole library UI) in order to render a read-only board. That is the one screen in this app
 * that must come up fast on hardware nobody chose and a connection nobody controls.
 *
 * Splitting costs one extra request on each route and saves the other two routes' code on all of them. The
 * shell above stays eager: auth and the share-token check must run without waiting for anything.
 */
const Workspace = lazy(() => import("./ui/workspace").then((m) => ({ default: m.Workspace })));
const WelcomePage = lazy(() => import("./ui/welcome-page").then((m) => ({ default: m.WelcomePage })));
const NewCampaignPage = lazy(() => import("./ui/new-campaign-page").then((m) => ({ default: m.NewCampaignPage })));
/** Kept here, not imported from the page: importing it would pull the welcome page into the first chunk. */
const NEW_CAMPAIGN_PATH = "/new-campaign";
const TableView = lazy(() => import("./ui/table-view").then((m) => ({ default: m.TableView })));
const HandoutView = lazy(() => import("./ui/handout-view").then((m) => ({ default: m.HandoutView })));
const CampaignView = lazy(() => import("./ui/campaign-view").then((m) => ({ default: m.CampaignView })));
const PlayerSheetView = lazy(() => import("./ui/player-sheet-view").then((m) => ({ default: m.PlayerSheetView })));
const PlayerHome = lazy(() => import("./ui/player-home").then((m) => ({ default: m.PlayerHome })));
const PlayerCharacters = lazy(() => import("./ui/player-characters").then((m) => ({ default: m.PlayerCharacters })));
const JoinPage = lazy(() => import("./ui/join-page").then((m) => ({ default: m.JoinPage })));

/**
 * Share links are the ONE route that must work without an account.
 *
 * Checked before anything auth-related runs: a share viewer has no session and never will, and making them
 * wait on a session lookup that will always come back empty would put a spinner on a TV screen at the table.
 * Deliberately not a router — one path prefix does not justify the dependency.
 */
function shareToken(): { kind: "table" | "handout" | "campaign" | "character"; token: string } | null {
  const p = window.location.pathname;
  const t = /^\/t\/([A-Za-z0-9_-]{8,})\/?$/.exec(p);
  if (t) return { kind: "table", token: t[1]! };
  const h = /^\/h\/([A-Za-z0-9_-]{8,})\/?$/.exec(p);
  if (h) return { kind: "handout", token: h[1]! };
  // The campaign's own page: what it is, and the documents the party has been given.
  const c = /^\/c\/([A-Za-z0-9_-]{8,})\/?$/.exec(p);
  if (c) return { kind: "campaign", token: c[1]! };
  // A player's own character sheet (migration 0047).
  const pc = /^\/p\/([A-Za-z0-9_-]{8,})\/?$/.exec(p);
  if (pc) return { kind: "character", token: pc[1]! };
  return null;
}

/**
 * The two pages an emailed link opens.
 *
 * ⚠ CHECKED BEFORE THE SESSION IS, and that ordering is the bug waiting to happen. Both links arrive carrying
 * credentials, so by the time this renders the visitor HAS a session — and a session-first check would send
 * somebody who came to choose a password straight into the workspace instead, with the password unset and no
 * sign of what went wrong.
 */
function passwordRoute(): "welcome" | "reset" | null {
  const p = window.location.pathname.replace(/\/$/, "");
  if (p === WELCOME_PATH) return "welcome";
  if (p === RESET_PATH) return "reset";
  return null;
}

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [checking, setChecking] = useState(true);
  const token = shareToken();
  const setting = passwordRoute();
  const invite = joinToken(window.location.pathname);

  useEffect(() => {
    if (!isConfigured || token || setting) {
      setChecking(false);
      return;
    }
    // Resolve the CURRENT session first, then subscribe. Subscribing alone is not enough: onAuthStateChange
    // does not replay what happened before it was attached, so a returning user with a valid session would sit
    // on the sign-in screen until something else woke the listener.
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setChecking(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  if (!isConfigured) return <NotConfigured missing={missingConfig()} />;
  // ⚠ The fallback says what is happening rather than flashing an empty page. On a television at the table
  // this is the only frame anyone sees before the board, so a bare white flash reads as a broken link.
  if (token) {
    return (
      <Suspense fallback={<Splash>Opening the board…</Splash>}>
        {token.kind === "character" ? <PlayerSheetView token={token.token} />
          : token.kind === "handout" ? <HandoutView token={token.token} />
          : token.kind === "campaign" ? <CampaignView token={token.token} />
          : <TableView token={token.token} />}
        {/* The 3D dice overlay on the surfaces that roll (table screen + a player's own sheet). Inert until a roll. */}
        {(token.kind === "table" || token.kind === "character") && <DiceLayer />}
        {/* The map screen has its own seat bar with the way back (seat-bar.tsx); the campaign page needs this one. */}
        {token.kind === "campaign" && cameFromSeat(window.location.search) && <SeatBack />}
        {/* A share-link page knows only its token; the database turns it into the campaign (0076). */}
        <FeedbackButton shareToken={token.token} />
        {/* A signed-in player opening their own sheet (/p) or arriving at the board from their seat (/t) resumes the tour here. */}
        {token.kind === "character" && <TourHost surface="sheet" />}
        {token.kind === "table" && <TourHost surface="table" enabled={cameFromSeat(window.location.search)} autoStart={isNewPlayer()} />}
      </Suspense>
    );
  }
  if (setting) return <SetPassword mode={setting} />;
  if (checking) return <Splash>Checking your session…</Splash>;
  // ⚠ THE INVITE PAGE WORKS SIGNED IN OR NOT — it is where a player without an account makes one.
  if (invite) {
    return (
      <Suspense fallback={<Splash>Opening your invite…</Splash>}>
        <JoinPage token={invite} session={session} />
        <FeedbackButton />
      </Suspense>
    );
  }
  if (!session) return <SignIn playing={!!pendingInvite()} />;
  // An account made from an invite comes back through /welcome and lands here: send it on to the invite it came from.
  const waiting = pendingInvite();
  if (waiting) {
    window.location.replace(`/join/${waiting}`);
    return <Splash>Opening your invite…</Splash>;
  }
  return <SignedIn email={session.user.email ?? ""} userId={session.user.id} />;
}

/**
 * GM or player? A player's account (a seat at someone's table, no campaign of its own) opens on its player home.
 *
 * ⚠⚠ THE GM'S WORKSPACE MUST NOT WAIT ON THIS. The answer is remembered per account on this device, so a GM's next load
 * opens the workspace at once and the check runs behind it; only the very first load of an account waits.
 */

function SignedIn({ email, userId }: { email: string; userId: string }) {
  const path = window.location.pathname.replace(/\/$/, "");
  // ⚠ THE FRONT DOOR (2026-09-19): the BARE address is the welcome page — campaigns you run, campaigns you play in, and a card
  // to start a new one. Anything that names a page (`?view=`, `?workspace=1`, `?scene=`…) is the workspace as before, so a
  // refresh in the middle of work stays in the work.
  const search = new URLSearchParams(window.location.search);
  const bare = path === "" && !["workspace", "view", "scene", "char", "card", "settings", "seed"].some((k) => search.has(k));
  const onPlay = path === PLAY_PATH;
  // "Start a campaign" / "Your own campaigns" from a player's home: the workspace, whatever the account usually opens on.
  const wantsWorkspace = new URLSearchParams(window.location.search).get("workspace") === "1";
  const [role, setRole] = useState<Role | null>(() => rememberedRole(userId));
  useEffect(() => {
    let alive = true;
    void myPlay().then((d) => {
      if (!alive || !d) { if (alive && !d) setRole((r) => r ?? "gm"); return; }
      const next = roleOf(d);
      rememberRole(userId, next);
      setRole(next);
    });
    return () => { alive = false; };
  }, [userId]);

  // A player's own characters and the Character Generator (0054) — any signed-in account may use it.
  // ⚠ Which campaign a note is about is read WHEN THE NOTE OPENS (the workspace switches without remounting): the
  // GM's current campaign on this device, else the table a player last sat at (player-home.tsx keeps that key).
  const currentCampaign = () => {
    const mine = loadCurrentCampaign(userId);
    if (mine) return mine;
    try { return localStorage.getItem("compendium.play.lastCampaign"); } catch { return null; }
  };
  // (after every hook above: the two pages below are whole pages, chosen by the address alone)
  if (path === NEW_CAMPAIGN_PATH) return <Suspense fallback={<Splash>Opening a new campaign…</Splash>}><NewCampaignPage userId={userId} /><TourHost surface="new-campaign" /></Suspense>;
  if (bare) return <Suspense fallback={<Splash>Finding your campaigns…</Splash>}><WelcomePage email={email} userId={userId} /><WhatsNew autoOpen /></Suspense>;
  if (path === PLAY_CHARACTERS_PATH) {
    return (
      <Suspense fallback={<Splash>Opening your characters…</Splash>}>
        <PlayerCharacters email={email} userId={userId} />
        <FeedbackButton campaignId={currentCampaign} />
      </Suspense>
    );
  }
  if (onPlay || (role === "player" && !wantsWorkspace)) {
    if (!onPlay) window.history.replaceState(null, "", PLAY_PATH);
    return (
      <Suspense fallback={<Splash>Finding your seat…</Splash>}>
        <PlayerHome email={email} userId={userId} />
        <DiceLayer />
        <WhatsNew autoOpen />
        <FeedbackButton campaignId={currentCampaign} />
        <TourHost surface="play" autoStart={isNewPlayer()} />
        <TutorialsPage role="player" />
      </Suspense>
    );
  }
  if (role === null && !wantsWorkspace) return <Splash>Checking your session…</Splash>;
  return (
    <Suspense fallback={<Splash>Loading your workspace…</Splash>}>
      <Workspace email={email} userId={userId} playing={role === "gm+player"} />
      <DiceLayer />
      <WhatsNew autoOpen />
      <FeedbackButton campaignId={currentCampaign} />
      <TourHost surface="workspace" />
      <TutorialsPage role="gm" />
    </Suspense>
  );
}

/** On /t and /c opened from a player's home: the one way back, out of the page's own way. */
function SeatBack() {
  return <a className="seat-back" href={PLAY_PATH}>← Your campaign</a>;
}

function NotConfigured({ missing }: { missing: string[] }) {
  return (
    <main className="pane">
      <h1>Campaign Compendium isn&rsquo;t configured for this deployment.</h1>
      <p>
        {missing.map((m) => <code key={m}>{m}</code>).reduce<React.ReactNode[]>(
          (acc, el, i) => (i === 0 ? [el] : [...acc, " and ", el]), [],
        )}{" "}
        {missing.length > 1 ? "are" : "is"} not set here, so the app cannot reach its database.
      </p>
      <p className="muted">
        Netlify scopes environment variables — a Deploy Preview does not inherit a production-only value. Set
        them for this context and redeploy. No data has been touched.
      </p>
    </main>
  );
}

/**
 * The wait before anything is on screen. The wordmark rides above the message as a CSS MASK, not an
 * image (2026-10-09, "in the offset color to whatever color choices the user has made"): a mask takes
 * its colour from `--ink`, so it follows the ground and accent the person chose in Display settings
 * instead of being a fixed brown or gold. It also keeps 67 KB of path data out of the JS bundle.
 */
function Splash({ children }: { children: React.ReactNode }) {
  return (
    <main className="pane">
      <div className="brandmark" role="img" aria-label="Campaign Compendium" />
      <p className="muted">{children}</p>
    </main>
  );
}
